"use client";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { Card, CircularProgress } from "@mui/material";
import { Formik } from "formik";
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import PageContainer from "@/components/atoms/PageContainer";
import AlertBlock from "@/components/molecules/AlertBlock";
import Suspense from "@/components/molecules/Suspense";
import PageLayout from "@/components/templates/PageLayout";
import { ORDER_PERMISSIONS } from "@/constants/modules.permissions";
import ROUTE_URLS from "@/constants/route-urls";
import useLanguage from "@/contexts/language-context";
import useGetOrders from "@/hooks/requests/orders/use-get-orders";
import { useButtonPermission } from "@/hooks/use-button-permission";
import useFilters from "@/hooks/use-filters";
import usePagination from "@/hooks/use-pagination";
import { IOrder } from "@/interfaces/order.interfaces";
import { OrdersMessages } from "@/messages/orders/order.messages";
import { IFilter } from "@/types/filter.types";

import OrderFiltersForm from "./components/OrderFiltersForm";
import { OrderTable } from "./components/OrderTable";
import { fixedExcludeQuotationFilter, initialFilterValues } from "./constants";

type Props = {
  messages: OrdersMessages;
};

export default function OrdersPage({ messages }: Props) {
  const pagination = usePagination();
  const { lang } = useLanguage();
  const { pageNumber, pageSize } = pagination;
  const filter = useFilters<IOrder>({ initialValue: initialFilterValues });
  const userFilters = filter.filters;
  const router = useNavigate();
  const orderPermission = useButtonPermission(ORDER_PERMISSIONS.CREATE);

  const hasOrderStatusSelected = userFilters.some(
    (f) => f.property === "orderStatus" && f.value
  );

  const finalFilters = useMemo(() => {
    if (hasOrderStatusSelected) {
      return userFilters;
    }
    return [...userFilters, fixedExcludeQuotationFilter];
  }, [userFilters, hasOrderStatusSelected]);

  const getOrders = useGetOrders({ filters: finalFilters, pageNumber, pageSize });
  const { error, data: orders, isLoading } = getOrders;

  const shouldLoadOrdersTable = !!orders && orders.items.length > 0 && !isLoading;
  const shouldLoadError = !orders && !!error && !isLoading;
  const shouldLoadNoOrdersFound = !!orders && orders.items.length === 0 && !error && !isLoading;

  const mapSkuToSkuCode = (filters: IFilter<IOrder>[]): IFilter<IOrder>[] => {
    return filters.map((filter) => {
      if (filter.property === "sku") {
        return { ...filter, property: "sku.code" };
      }
      return filter;
    });
  };

  const handleChangeFilters = (newFilters: IFilter<IOrder>[]) => {
    const mappedFilters = mapSkuToSkuCode(newFilters);
    filter.changeFilters(mappedFilters);
  };

  return (
    <PageLayout messages={messages} title={messages.ordersPageTitle}>
      <PageContainer>
        <Formik initialValues={userFilters} validateOnChange={false} onSubmit={handleChangeFilters}>
          <OrderFiltersForm messages={messages} />
        </Formik>

        <Card
          sx={({ functions: { pxToRem } }) => ({
            marginTop: pxToRem(16),
          })}
        >
          <Suspense isSuspended={isLoading} fallback={<CircularProgress size={50} />}>
            <MDBox p={2}>
              <MDBox p={2} display="flex" justifyContent="space-between" alignItems="center">
                <MDTypography variant="h6">{messages.ordersPageTitle}</MDTypography>

                <MDButton
                  variant="gradient"
                  color="dark"
                  onClick={() => router(ROUTE_URLS.createOrder(lang))}
                  disabled={orderPermission.disabled}
                  isLoading={orderPermission.loading}
                  endIcon={<AddCircleOutlineIcon />}
                >
                  {messages.addOrderButton}
                </MDButton>
              </MDBox>

              {shouldLoadOrdersTable && (
                <OrderTable pagination={pagination} data={orders} messages={messages} />
              )}

              {shouldLoadError && (
                <AlertBlock
                  title={messages.orderErrorToLoad}
                  description={error?.message}
                  alt={messages.orderNotFoundAlt}
                />
              )}
              {shouldLoadNoOrdersFound && (
                <AlertBlock
                  title={messages.orderNotFoundError}
                  description={messages.orderNotFoundDescription}
                  alt={messages.orderNotFoundAlt}
                />
              )}
            </MDBox>
          </Suspense>
        </Card>
      </PageContainer>
    </PageLayout>
  );
}
